// User answers/values
interface FormFieldData {
	portal_user_field_value_id?: string;
	name: string;
	custom_name?: string;
	rating?: number | null;
	value: any;
	portal_user_form?: string;
	field?: string;
	[key: string]: any;
}

interface FormFieldDataBackup {
	fieldData: FormFieldData[];
	timestamp: number;
	formId: string;
}

const FORM_FIELD_DATA_BACKUP_KEY = 'form_field_data_backup';
// Workaround key for iframe (portal client) context.
const IFRAME_FORM_FIELD_DATA_BACKUP_KEY = 'iframe_form_field_data_backup';

// Track forms that are navigating away to prevent race conditions
const navigationFlags = new Map<string, boolean>();

/**
 * Mark form as navigating to prevent backup creation
 */
export function markFormAsNavigating(formId: string): void {
	navigationFlags.set(formId, true);
}

/**
 * Clear navigation flag for form
 */
export function clearFormNavigationFlag(formId: string): void {
	navigationFlags.delete(formId);
}

/**
 * Check if form is marked as navigating
 */
export function isFormNavigating(formId: string): boolean {
	return navigationFlags.get(formId) === true;
}

/**
 * Check if the application is running inside an iframe
 */
function isIframe(): boolean {
	try {
		const isiFrame = new URLSearchParams(window.location.search).get('iFrame');
		return !!isiFrame;
	} catch (error) {
		console.error('Failed to determine if running in iframe:', error);
		return false;
	}
}

/**
 * Get the user id from localStorage user object
 */
function getUserId(): string | null {
	try {
		const userStr = localStorage.getItem('user');
		if (!userStr) {
			return null;
		}
		const user = JSON.parse(userStr);
		return user?.userID || user?.portalAccountId || null;
	} catch (error) {
		console.error('Failed to get user id:', error);
		return null;
	}
}

/**
 * Get the appropriate backup key based on iframe context and user identity
 */
function getBackupKeyPrefix(): string | null {
	const basePrefix = isIframe() ? IFRAME_FORM_FIELD_DATA_BACKUP_KEY : FORM_FIELD_DATA_BACKUP_KEY;
	const userId = getUserId();

	if (userId == null) {
		console.warn('User ID not found.');
		return null;
	}

	return `${basePrefix}_${userId}`;
}

/**
 * Utility function to scan localStorage keys with a filter predicate
 */
function scanLocalStorageKeys(predicate: (key: string) => boolean): string[] {
	const keys: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key && predicate(key)) {
			keys.push(key);
		}
	}
	return keys;
}

/**
 * Clean up previous form field data backups for a form
 */
function cleanupPreviousFormFieldDataBackups(formId: string): void {
	try {
		const keyPrefix = getBackupKeyPrefix();
		if (keyPrefix == null) {
			return;
		}
		const keysToRemove = scanLocalStorageKeys(
			(key) => key.startsWith(keyPrefix) && key.includes(`_${formId}`)
		);

		keysToRemove.forEach((key) => {
			localStorage.removeItem(key);
		});
	} catch (error) {
		console.error('Failed to cleanup previous form field data backups:', error);
	}
}

/**
 * Save form field data backup to localStorage.
 */
export function saveFormFieldDataBackup(fieldData: FormFieldData[], formId: string): void {
	try {
		if (!formId) {
			console.warn('Form ID is required for saving form field data backup');
			return;
		}

		// Prevent backup creation if form is navigating away
		if (isFormNavigating(formId)) {
			return;
		}

		const keyPrefix = getBackupKeyPrefix();

		if (keyPrefix == null) {
			console.warn('Key prefix is required for saving form field data backup');
			return;
		}

		const backupKey = `${keyPrefix}_${formId}`;

		// Clean up previous backups before saving new one
		cleanupPreviousFormFieldDataBackups(formId);

		const backupData: FormFieldDataBackup = {
			fieldData,
			timestamp: Date.now(),
			formId
		};

		localStorage.setItem(backupKey, JSON.stringify(backupData));
	} catch (error) {
		console.error('Failed to save form field data backup:', error);
	}
}

/**
 * Get form field data backup from localStorage
 */
export function getFormFieldDataBackup(formId: string): FormFieldDataBackup | null {
	try {
		if (!formId) {
			console.warn('Form ID is required for getting form field data backup');
			return null;
		}

		const keyPrefix = getBackupKeyPrefix();
		if (keyPrefix == null) {
			return null;
		}
		const backupKey = `${keyPrefix}_${formId}`;
		const backupData = localStorage.getItem(backupKey);
		return backupData ? (JSON.parse(backupData) as FormFieldDataBackup) : null;
	} catch (error) {
		console.error('Failed to get form field data backup:', error);
		return null;
	}
}

/**
 * Clear form field data backup from localStorage
 */
export function clearFormFieldDataBackup(formId: string): void {
	try {
		if (!formId) {
			console.warn('Form ID is required for clearing form field data backup');
			return;
		}

		const keyPrefix = getBackupKeyPrefix();
		const backupKey = `${keyPrefix}_${formId}`;
		localStorage.removeItem(backupKey);

		clearFormNavigationFlag(formId);
	} catch (error) {
		console.error('Failed to clear form field data backup:', error);
	}
}

export type { FormFieldData, FormFieldDataBackup };
